/**
 * useExportSuppliers - Hook d'export CSV pour les fournisseurs
 *
 * Pattern: Component → Hook → Service (API)
 * Itère toutes les pages pour exporter tous les fournisseurs (pas juste la page courante)
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { suppliersAPI } from '@/utils/suppliersAPI';
import { downloadCSV, fetchAllPages, formatDateForExport, type ExportColumn } from '@/utils/exportUtils';
import { logger } from '@/utils/logger';
import { notify } from '@/stores/notificationStore';
import type { Supplier } from '@/types/models';

interface ExportSuppliersFilters {
  search?: string;
  includeInactive?: boolean;
}

export function useExportSuppliers() {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async (filters: ExportSuppliersFilters = {}) => {
    setIsExporting(true);
    try {
      const allSuppliers = await fetchAllPages<Supplier>(async (page) => {
        const response = await suppliersAPI.getAll({
          page,
          limit: 100,
          search: filters.search || undefined,
          includeInactive: filters.includeInactive,
        });
        return { data: response.suppliers, totalPages: response.pagination.pages };
      });

      const columns: ExportColumn<Supplier>[] = [
        { key: 'companyName', header: t('products.suppliers.table.company') },
        { key: 'contactName', header: t('products.suppliers.table.contact') },
        { key: 'email', header: t('products.suppliers.form.email') },
        { key: 'phone', header: t('products.suppliers.form.phone') },
        {
          key: 'type',
          header: t('common.labels.type'),
          formatter: (value) => value ? t(`products.suppliers.type.${value}`, { defaultValue: String(value) }) : '',
        },
        { key: 'sirenSiret', header: t('products.suppliers.table.siret') },
        { key: 'vatNumber', header: t('products.suppliers.form.vatNumber') },
        { key: 'tradeCategory', header: t('products.suppliers.export.tradeCategory') },
        { key: 'addressLine1', header: t('products.suppliers.form.addressLine1') },
        { key: 'city', header: t('products.suppliers.form.city') },
        { key: 'zipCode', header: t('products.suppliers.form.zipCode') },
        { key: 'countryCode', header: t('products.suppliers.form.countryCode') },
        {
          key: 'isActive',
          header: t('products.suppliers.table.status'),
          formatter: (value) => value ? t('products.suppliers.filters.active') : t('products.suppliers.filters.inactive'),
        },
        {
          key: 'createdAt',
          header: t('common.labels.createdAt'),
          formatter: (value) => formatDateForExport(value as string),
        },
      ];

      const today = new Date().toISOString().split('T')[0];
      downloadCSV(allSuppliers, columns, {
        filename: `fournisseurs_export_${today}`,
        format: 'csv',
      });
    } catch (error) {
      logger.error('Export suppliers error:', error);
      notify.error(t('common.export.error'), t('products.suppliers.errorTitle'));
    } finally {
      setIsExporting(false);
    }
  }, [t]);

  return { exportCSV, isExporting };
}
